import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { activityService } from '../../../services/activityService';
import { snagService } from '../../../services/snagService';
import { useAuth } from '../../../contexts/AuthContext';

const ACTIVITY_CONFIG = {
  task_completed: {
    icon: 'CheckCircle2',
    color: 'text-emerald-600',
    bg: 'bg-emerald-100',
    label: 'Task Completed',
    borderColor: 'border-emerald-200'
  },
  snag_created: {
    icon: 'AlertOctagon',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    label: 'Snag Raised',
    borderColor: 'border-amber-200'
  },
  snag_resolved: {
    icon: 'CheckSquare',
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    label: 'Snag Resolved',
    borderColor: 'border-blue-200'
  },
  snag_updated: {
    icon: 'Edit3',
    color: 'text-slate-600',
    bg: 'bg-slate-100',
    label: 'Snag Updated',
    borderColor: 'border-slate-200'
  },
  incident_created: {
    icon: 'ShieldAlert',
    color: 'text-red-600',
    bg: 'bg-red-100',
    label: 'Incident Reported',
    borderColor: 'border-red-200'
  },
  document_uploaded: {
    icon: 'FileUp',
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    label: 'Document Uploaded',
    borderColor: 'border-purple-200'
  },
  document_expiring: {
    icon: 'Clock',
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    label: 'Document Expiring',
    borderColor: 'border-orange-200'
  },
  site_created: {
    icon: 'Building2',
    color: 'text-accent',
    bg: 'bg-accent/10',
    label: 'Site Created',
    borderColor: 'border-accent/20'
  },
  site_updated: {
    icon: 'Settings',
    color: 'text-slate-600',
    bg: 'bg-slate-100',
    label: 'Site Updated',
    borderColor: 'border-slate-200'
  },
  worker_assigned: {
    icon: 'UserPlus',
    color: 'text-teal-600',
    bg: 'bg-teal-100',
    label: 'Worker Assigned',
    borderColor: 'border-teal-200'
  },
  default: {
    icon: 'Activity',
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    label: 'Activity',
    borderColor: 'border-border'
  }
};

// Event type groups for the filter
const EVENT_TYPE_GROUPS = [
  { value: 'all', label: 'All Events', icon: 'LayoutList', types: null },
  { value: 'tasks', label: 'Tasks', icon: 'CheckCircle2', types: ['task_completed'] },
  { value: 'snags', label: 'Snags', icon: 'AlertOctagon', types: ['snag_created', 'snag_updated', 'snag_resolved'] },
  { value: 'incidents', label: 'Incidents', icon: 'ShieldAlert', types: ['incident_created'] },
  { value: 'documents', label: 'Documents', icon: 'FileUp', types: ['document_uploaded', 'document_expiring'] }
];

const formatRelativeTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatFullDate = (dateStr) => {
  return new Date(dateStr)?.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ActivityCard = ({ activity, isNew }) => {
  const config = ACTIVITY_CONFIG?.[activity?.activity_type] || ACTIVITY_CONFIG?.default;
  const userName = activity?.user?.full_name || 'Unknown User';
  const userRole = activity?.user?.role || '';
  const [photoUrl, setPhotoUrl] = useState(activity?.photo_url || null);
  const [photoLoading, setPhotoLoading] = useState(false);

  useEffect(() => {
    const fetchSnagPhoto = async () => {
      if (activity?.photo_url) return;
      const meta = activity?.metadata;
      if (!meta?.snagId && !meta?.photoPaths?.length) return;
      if (meta?.photoPaths?.length > 0) {
        setPhotoLoading(true);
        try {
          const { url } = await snagService?.getSnagPhotoUrl(meta?.photoPaths?.[0]);
          if (url) setPhotoUrl(url);
        } catch {}
        setPhotoLoading(false);
      }
    };
    fetchSnagPhoto();
  }, [activity?.metadata, activity?.photo_url]);

  return (
    <div className={`flex gap-3 p-4 rounded-xl border ${config?.borderColor} bg-card hover:shadow-sm transition-all ${
      isNew ? 'ring-2 ring-accent/40 animate-pulse-once' : ''
    }`}>
      <div className={`flex-shrink-0 w-9 h-9 rounded-full ${config?.bg} flex items-center justify-center mt-0.5`}>
        <Icon name={config?.icon} size={16} className={config?.color} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex-1 min-w-0">
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${config?.bg} ${config?.color} mb-1`}>
              {isNew && <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />}
              {config?.label}
            </span>
            <p className="text-sm font-semibold text-foreground leading-snug">{activity?.title}</p>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0" title={formatFullDate(activity?.created_at)}>
            {formatRelativeTime(activity?.created_at)}
          </span>
        </div>
        {activity?.description && (
          <p className="text-xs text-muted-foreground mb-2 leading-relaxed">{activity?.description}</p>
        )}
        {photoLoading && (
          <div className="w-24 h-16 rounded-lg bg-muted animate-pulse mb-2" />
        )}
        {photoUrl && !photoLoading && (
          <div className="mb-2">
            <img
              src={photoUrl}
              alt={`Photo for ${activity?.title}`}
              className="w-24 h-16 object-cover rounded-lg border border-border cursor-pointer hover:opacity-90 transition-opacity"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}
        <div className="flex items-center gap-3 mt-1">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
              <Icon name="User" size={10} className="text-accent" />
            </div>
            <span className="text-xs text-muted-foreground">
              {userName}
              {userRole && <span className="ml-1 capitalize text-muted-foreground/60">({userRole?.replace('_', ' ')})</span>}
            </span>
          </div>
          {activity?.site?.site_name && (
            <div className="flex items-center gap-1">
              <Icon name="MapPin" size={10} className="text-muted-foreground/60" />
              <span className="text-xs text-muted-foreground/60">{activity?.site?.site_name}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SiteActivityTimeline = ({ siteId }) => {
  const { userProfile } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newActivityIds, setNewActivityIds] = useState(new Set());

  // Filter state
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const unsubscribeRef = useRef(null);

  // Compute active types array from group selection
  const getActiveTypes = useCallback(() => {
    const group = EVENT_TYPE_GROUPS?.find(g => g?.value === eventTypeFilter);
    return group?.types || null;
  }, [eventTypeFilter]);

  const loadActivities = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const activeTypes = getActiveTypes();
      const { data, error: fetchError } = await activityService?.getActivitiesBySite(siteId, {
        activityType: activeTypes || undefined,
        userId: userFilter !== 'all' ? userFilter : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: 200
      });
      if (fetchError) throw fetchError;
      setActivities(data || []);
    } catch (err) {
      console.error('[SiteActivityTimeline] load timeline failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading timeline: ${err?.message}`);
      } else {
        setError(`Failed to load activity timeline: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, eventTypeFilter, userFilter, dateFrom, dateTo, getActiveTypes]);

  // Load users for filter dropdown
  const loadUsers = useCallback(async () => {
    if (!siteId) return;
    const { data, error: err } = await activityService?.getActivityUsersBySite(siteId);
    if (err) {
      console.error('[SiteActivityTimeline] load users failed:', { code: err?.code, message: err?.message });
    }
    setAvailableUsers(data || []);
  }, [siteId]);

  // Initial load + reload on filter change
  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Real-time subscription
  useEffect(() => {
    if (!siteId) return;

    // Unsubscribe previous channel
    if (unsubscribeRef?.current) {
      unsubscribeRef?.current();
    }

    const unsubscribe = activityService?.subscribeToSiteActivities(siteId, (newActivity) => {
      setActivities(prev => {
        // Avoid duplicates
        if (prev?.some(a => a?.id === newActivity?.id)) return prev;
        return [newActivity, ...prev];
      });
      // Mark as new for highlight animation
      setNewActivityIds(prev => new Set([...prev, newActivity.id]));
      // Remove highlight after 5 seconds
      setTimeout(() => {
        setNewActivityIds(prev => {
          const next = new Set(prev);
          next?.delete(newActivity?.id);
          return next;
        });
      }, 5000);
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef?.current) {
        unsubscribeRef?.current();
        unsubscribeRef.current = null;
      }
    };
  }, [siteId]);

  // Group activities by date
  const groupedActivities = activities?.reduce((groups, activity) => {
    const date = new Date(activity?.created_at)?.toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
    if (!groups?.[date]) groups[date] = [];
    groups?.[date]?.push(activity);
    return groups;
  }, {});

  const totalCount = activities?.length;
  const hasActiveFilters = eventTypeFilter !== 'all' || userFilter !== 'all' || dateFrom || dateTo;

  const clearFilters = () => {
    setEventTypeFilter('all');
    setUserFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">Site Activity Timeline</h3>
            <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {totalCount} event{totalCount !== 1 ? 's' : ''}{hasActiveFilters ? ' (filtered)' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowFilters(v => !v)}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
            showFilters || hasActiveFilters
              ? 'bg-accent text-accent-foreground border-accent'
              : 'text-muted-foreground border-border hover:bg-muted hover:text-foreground'
          }`}
        >
          <Icon name="SlidersHorizontal" size={13} />
          Filters
          {hasActiveFilters && (
            <span className="ml-1 w-4 h-4 rounded-full bg-white/30 text-[10px] font-bold flex items-center justify-center">
              {[eventTypeFilter !== 'all', userFilter !== 'all', !!dateFrom, !!dateTo]?.filter(Boolean)?.length}
            </span>
          )}
        </button>
      </div>
      {/* Event Type Pills (always visible) */}
      <div className="flex flex-wrap gap-2">
        {EVENT_TYPE_GROUPS?.map(group => (
          <button
            key={group?.value}
            onClick={() => setEventTypeFilter(group?.value)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              eventTypeFilter === group?.value
                ? 'bg-accent text-accent-foreground border-accent'
                : 'bg-background text-muted-foreground border-border hover:border-accent hover:text-accent'
            }`}
          >
            <Icon name={group?.icon} size={12} />
            {group?.label}
          </button>
        ))}
      </div>
      {/* Expanded Filters Panel */}
      {showFilters && (
        <div className="bg-muted/40 border border-border rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Date From */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e?.target?.value)}
                max={dateTo || undefined}
                className="w-full text-xs border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
              />
            </div>
            {/* Date To */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e?.target?.value)}
                min={dateFrom || undefined}
                className="w-full text-xs border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
              />
            </div>
            {/* Worker / User */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Worker / User</label>
              <select
                value={userFilter}
                onChange={e => setUserFilter(e?.target?.value)}
                className="w-full text-xs border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
              >
                <option value="all">All Users</option>
                {availableUsers?.map(u => (
                  <option key={u?.id} value={u?.id}>
                    {u?.full_name} {u?.role ? `(${u?.role?.replace('_', ' ')})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-accent hover:underline font-medium flex items-center gap-1"
            >
              <Icon name="X" size={11} />
              Clear all filters
            </button>
          )}
        </div>
      )}
      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Icon name="Loader2" size={32} className="text-accent animate-spin" />
          <p className="text-sm text-muted-foreground">Loading activity timeline...</p>
        </div>
      )}
      {/* Error */}
      {!loading && error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
          <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-700">Failed to load timeline</p>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </div>
          <button
            onClick={() => loadActivities()}
            className="ml-auto text-xs font-semibold text-red-700 hover:underline"
          >
            Retry
          </button>
        </div>
      )}
      {/* Empty state */}
      {!loading && !error && activities?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
            <Icon name="Activity" size={24} className="text-muted-foreground" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">No activity found</p>
            <p className="text-xs text-muted-foreground mt-1">
              {hasActiveFilters
                ? 'No events match the selected filters. Try adjusting or clearing them.' :'Activity will appear here as tasks are completed, snags are raised, incidents are reported, and documents are uploaded.'}
            </p>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs font-medium text-accent hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
      {/* Timeline grouped by date */}
      {!loading && !error && activities?.length > 0 && (
        <div className="space-y-6">
          {Object?.entries(groupedActivities)?.map(([date, dayActivities]) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-semibold text-muted-foreground px-2 py-1 bg-muted rounded-full whitespace-nowrap">
                  {date}
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="space-y-3">
                {dayActivities?.map(activity => (
                  <ActivityCard
                    key={activity?.id}
                    activity={activity}
                    isNew={newActivityIds?.has(activity?.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SiteActivityTimeline;
